// PropertyReviews — the reviews section embedded under the property
// detail tabs in Explore.tsx PropertyView (PROP-72 / PROP-75).
//
// Layout:
//   • header: avg ★ + total count (or zero-state copy)
//   • compose box (signed-in only) — 1–5★ + textarea + submit
//     signed-out users see a "Sign in to review" CTA routing to /auth
//   • paginated list of visible reviews:
//       author · ★ · body · relative time
//       overflow "⋯" menu — Edit / Delete (own) or Report (others)
//   • delete confirm modal, report-reason dialog, local toast
//
// Optimistic-UI symmetry (CLAUDE.md): create / update / delete patch the
// in-memory rows and aggregate from the SERVER RESPONSE — never from
// the form state. Aggregate math runs through the pure recompute
// helpers in reviewsApi so the live view equals the post-refresh view
// to the last decimal.
//
// All visible strings go through i18next (reviews.*); the report-reason
// enum mirrors the backend's closed set.

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/lib/api";
import { useAuthUser, type AuthUser } from "@/lib/auth";
import { relativeTime } from "@/lib/agentCases";
import {
  REVIEW_BODY_MAX_LEN,
  REVIEW_RATING_MAX,
  REVIEW_RATING_MIN,
  REVIEW_REPORT_REASONS,
  aggregateAfterCreate,
  aggregateAfterDelete,
  aggregateAfterEdit,
  createReview,
  deleteReview,
  listReviews,
  reportReview,
  reviewBodyByteLength,
  updateReview,
  validateReviewInput,
  type Review,
  type ReviewAggregate,
  type ReviewReportReason,
} from "@/lib/reviewsApi";

const PAGE_LIMIT = 20;

export function PropertyReviews({
  projectName,
  onAggregateChange,
}: {
  projectName: string;
  // Fires whenever the live aggregate changes — first-page load,
  // create / update / delete / load-more. PropertyView in Explore.tsx
  // uses it to keep the modal header's ReviewBadge in lockstep with
  // the section's running aggregate so the live view equals the
  // post-refresh view (CLAUDE.md symmetry rule). The header would
  // otherwise lag a re-fetch behind every mutation.
  onAggregateChange?: (avg: number, count: number) => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuthUser();
  const navigate = useNavigate();

  // Feed state.
  const [reviews, setReviews] = useState<Review[]>([]);
  const [agg, setAgg] = useState<ReviewAggregate>({ avg: 0, count: 0 });
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [feedStatus, setFeedStatus] = useState<"loading" | "ready" | "error">("loading");
  const [loadMoreBusy, setLoadMoreBusy] = useState(false);
  const [loadMoreErr, setLoadMoreErr] = useState<string | null>(null);

  // Compose state.
  const [composeRating, setComposeRating] = useState(0);
  const [composeBody, setComposeBody] = useState("");
  const [composeBusy, setComposeBusy] = useState(false);
  const [composeErr, setComposeErr] = useState<string | null>(null);

  // Inline edit — only one row open at a time.
  const [editId, setEditId] = useState<string | null>(null);
  const [editRating, setEditRating] = useState(0);
  const [editBody, setEditBody] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState<string | null>(null);

  // Delete confirm dialog.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Report dialog.
  const [reportingId, setReportingId] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState<ReviewReportReason>("spam");
  const [reportNote, setReportNote] = useState("");
  const [reportBusy, setReportBusy] = useState(false);

  // Overflow menu — id of the review whose ⋯ menu is open. Mutually
  // exclusive so we don't have to manage a per-row open flag.
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Local toast (auto-dismiss after 3s).
  const [toast, setToast] = useState<string | null>(null);

  // onAggregateChangeRef decouples the callback identity from the
  // effect that fires it — a parent passing an inline closure (the
  // common case) re-renders this child with a new function identity
  // every time, which would otherwise re-fire the aggregate-sync
  // effect, re-set the parent state, re-render, re-fire — an
  // infinite loop. Storing the callback in a ref lets us depend only
  // on the actual aggregate values.
  const onAggregateChangeRef = useRef(onAggregateChange);
  useEffect(() => {
    onAggregateChangeRef.current = onAggregateChange;
  }, [onAggregateChange]);

  // currentProjectRef pins the project the component is currently
  // showing. Every handler captures projectName in its closure when it
  // fires; if the user navigates to another property before the promise
  // resolves, currentProjectRef has already moved on and the
  // resolution must NOT inject that property's row into this one's
  // state. The first-page fetch has its own `cancelled` flag below;
  // this ref covers create / update / delete / report / load-more
  // resolutions which `cancelled` doesn't reach.
  const currentProjectRef = useRef(projectName);

  // Track whether the user already has a review so we can hide the
  // compose box (the API enforces UNIQUE one-per-user-per-project; the
  // 409 is a fine fallback but a clean UI avoids the round trip).
  const ownReview = useMemo(
    () => (user ? reviews.find((r) => r.author_user_id === user.id) ?? null : null),
    [reviews, user],
  );

  // Fetch on mount / projectName change. Also resets every transient
  // dialog / form so a half-finished interaction on project A doesn't
  // bleed into project B (e.g. a Compose-box draft would otherwise
  // pre-fill on the next property).
  useEffect(() => {
    let cancelled = false;
    currentProjectRef.current = projectName;
    setFeedStatus("loading");
    setReviews([]);
    setAgg({ avg: 0, count: 0 });
    setNextCursor(undefined);
    setLoadMoreErr(null);
    // Wipe transient interaction state too — see ref comment above.
    setComposeRating(0);
    setComposeBody("");
    setComposeErr(null);
    setEditId(null);
    setEditErr(null);
    setDeletingId(null);
    setReportingId(null);
    setReportNote("");
    setOpenMenuId(null);
    listReviews(projectName, { limit: PAGE_LIMIT })
      .then((resp) => {
        if (cancelled) return;
        setReviews(resp.reviews ?? []);
        setAgg(resp.aggregate);
        setNextCursor(resp.next_cursor);
        setFeedStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setFeedStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [projectName]);

  // Aggregate-sync to the parent (PropertyView header badge). Only
  // fires after the first-page fetch lands (feedStatus === "ready"),
  // so the parent's existing detail.reviewAvg/Count isn't clobbered
  // with the {0,0} default while the section is still loading.
  useEffect(() => {
    if (feedStatus !== "ready") return;
    onAggregateChangeRef.current?.(agg.avg, agg.count);
  }, [feedStatus, agg.avg, agg.count]);

  // Toast auto-dismiss.
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(id);
  }, [toast]);

  // Close overflow menus on outside click / Escape.
  useEffect(() => {
    if (!openMenuId) return;
    const onClickAway = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t?.closest('[data-pwreview-menu]')) setOpenMenuId(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenMenuId(null);
    };
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("keydown", onKey);
    };
  }, [openMenuId]);

  // ── handlers ─────────────────────────────────────────────────────────

  function handleSubmitCompose() {
    setComposeErr(null);
    const v = validateReviewInput(composeRating, composeBody);
    if (!v.ok) {
      setComposeErr(t(v.errKey));
      return;
    }
    setComposeBusy(true);
    const submittedProject = projectName;
    createReview(submittedProject, { rating: v.rating, body: v.body })
      .then((row) => {
        // Stale-resolution guard: bail without touching state if the
        // user has navigated to a different property mid-flight.
        if (currentProjectRef.current !== submittedProject) return;
        // Patch state from the server row — never from form values.
        setReviews((prev) => [row, ...prev]);
        setAgg((prev) => aggregateAfterCreate(prev, row.rating));
        setComposeRating(0);
        setComposeBody("");
        setToast(t("reviews.toastCreated"));
      })
      .catch((err: unknown) => {
        if (currentProjectRef.current !== submittedProject) return;
        const status = err instanceof ApiError ? err.status : undefined;
        if (status === 401) {
          // Session expired mid-compose — bounce to /auth.
          navigate("/auth");
        } else if (status === 409) {
          setComposeErr(t("reviews.errDuplicate"));
        } else if (status === 404) {
          setComposeErr(t("reviews.errProject"));
        } else {
          setComposeErr(t("reviews.errGeneric"));
        }
      })
      .finally(() => {
        if (currentProjectRef.current !== submittedProject) return;
        setComposeBusy(false);
      });
  }

  function handleStartEdit(r: Review) {
    setOpenMenuId(null);
    setEditId(r.id);
    setEditRating(r.rating);
    setEditBody(r.body);
    setEditErr(null);
  }

  function handleCancelEdit() {
    setEditId(null);
    setEditErr(null);
  }

  function handleSubmitEdit() {
    if (!editId) return;
    setEditErr(null);
    const v = validateReviewInput(editRating, editBody);
    if (!v.ok) {
      setEditErr(t(v.errKey));
      return;
    }
    const oldRow = reviews.find((r) => r.id === editId);
    if (!oldRow) {
      // Row vanished under us (deleted in another tab, etc.) — reset.
      setEditId(null);
      return;
    }
    setEditBusy(true);
    const submittedProject = projectName;
    updateReview(submittedProject, editId, { rating: v.rating, body: v.body })
      .then((row) => {
        if (currentProjectRef.current !== submittedProject) return;
        setReviews((prev) => prev.map((r) => (r.id === row.id ? row : r)));
        setAgg((prev) => aggregateAfterEdit(prev, oldRow.rating, row.rating));
        setEditId(null);
        setToast(t("reviews.toastUpdated"));
      })
      .catch((err: unknown) => {
        if (currentProjectRef.current !== submittedProject) return;
        const status = err instanceof ApiError ? err.status : undefined;
        if (status === 401) {
          navigate("/auth");
        } else if (status === 403) {
          setEditErr(t("reviews.errForbidden"));
        } else if (status === 404) {
          // Row was deleted on the server side — drop it locally.
          setReviews((prev) => prev.filter((r) => r.id !== editId));
          setAgg((prev) => aggregateAfterDelete(prev, oldRow.rating));
          setEditId(null);
          setToast(t("reviews.errGoneRefresh"));
        } else {
          setEditErr(t("reviews.errGeneric"));
        }
      })
      .finally(() => {
        if (currentProjectRef.current !== submittedProject) return;
        setEditBusy(false);
      });
  }

  function handleAskDelete(id: string) {
    setOpenMenuId(null);
    setDeletingId(id);
  }

  function handleConfirmDelete() {
    if (!deletingId) return;
    const removed = reviews.find((r) => r.id === deletingId);
    if (!removed) {
      setDeletingId(null);
      return;
    }
    setDeleteBusy(true);
    const submittedProject = projectName;
    deleteReview(submittedProject, deletingId)
      .then(() => {
        if (currentProjectRef.current !== submittedProject) return;
        setReviews((prev) => prev.filter((r) => r.id !== deletingId));
        setAgg((prev) => aggregateAfterDelete(prev, removed.rating));
        setDeletingId(null);
        setToast(t("reviews.toastDeleted"));
      })
      .catch((err: unknown) => {
        if (currentProjectRef.current !== submittedProject) return;
        const status = err instanceof ApiError ? err.status : undefined;
        if (status === 401) {
          navigate("/auth");
        } else if (status === 404) {
          // Already gone — sync local state and tell the user.
          setReviews((prev) => prev.filter((r) => r.id !== deletingId));
          setAgg((prev) => aggregateAfterDelete(prev, removed.rating));
          setDeletingId(null);
          setToast(t("reviews.errGoneRefresh"));
        } else {
          setToast(t("reviews.errGeneric"));
        }
      })
      .finally(() => {
        if (currentProjectRef.current !== submittedProject) return;
        setDeleteBusy(false);
      });
  }

  function handleAskReport(id: string) {
    setOpenMenuId(null);
    setReportingId(id);
    setReportReason("spam");
    setReportNote("");
  }

  function handleConfirmReport() {
    if (!reportingId) return;
    setReportBusy(true);
    const submittedProject = projectName;
    reportReview(submittedProject, reportingId, {
      reason: reportReason,
      note: reportNote.trim() || undefined,
    })
      .then((resp) => {
        if (currentProjectRef.current !== submittedProject) return;
        setReportingId(null);
        setReportNote("");
        setToast(
          resp.already_reported
            ? t("reviews.toastReportedDup")
            : t("reviews.toastReported"),
        );
      })
      .catch((err: unknown) => {
        if (currentProjectRef.current !== submittedProject) return;
        const status = err instanceof ApiError ? err.status : undefined;
        if (status === 401) {
          navigate("/auth");
        } else if (status === 404) {
          // Review already gone.
          setReviews((prev) => prev.filter((r) => r.id !== reportingId));
          setReportingId(null);
          setToast(t("reviews.errGoneRefresh"));
        } else {
          setToast(t("reviews.errGeneric"));
        }
      })
      .finally(() => {
        if (currentProjectRef.current !== submittedProject) return;
        setReportBusy(false);
      });
  }

  function handleLoadMore() {
    if (!nextCursor || loadMoreBusy) return;
    setLoadMoreErr(null);
    setLoadMoreBusy(true);
    const submittedProject = projectName;
    listReviews(submittedProject, { limit: PAGE_LIMIT, cursor: nextCursor })
      .then((resp) => {
        if (currentProjectRef.current !== submittedProject) return;
        setReviews((prev) => [...prev, ...(resp.reviews ?? [])]);
        setNextCursor(resp.next_cursor);
      })
      .catch(() => {
        if (currentProjectRef.current !== submittedProject) return;
        setLoadMoreErr(t("reviews.loadMoreError"));
      })
      .finally(() => {
        if (currentProjectRef.current !== submittedProject) return;
        setLoadMoreBusy(false);
      });
  }

  // ── render ───────────────────────────────────────────────────────────

  const headerCount = agg.count;
  const headerAvg = agg.count > 0 ? agg.avg : 0;
  // Counters surface UTF-8 byte length — same metric the backend caps
  // against — so a Chinese reviewer's bar doesn't lie about how close
  // they are to the limit (each CJK char is ~3 UTF-8 bytes).
  const composeByteCount = reviewBodyByteLength(composeBody);
  const editByteCount = reviewBodyByteLength(editBody);

  return (
    <section
      aria-label={t("reviews.title")}
      style={{
        marginTop: 36,
        paddingTop: 28,
        borderTop: "1px solid var(--rule)",
        fontFamily: "var(--font-ui)",
      }}
    >
      <header style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ai-accent)", marginBottom: 6 }}>
            {t("reviews.title")}
          </div>
          {headerCount > 0 ? (
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontFamily: "var(--font-serif)", fontSize: 28, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.02em" }}>
                {headerAvg.toFixed(1)}
              </span>
              <span aria-hidden style={{ color: "var(--accent)", fontSize: 16 }}>
                {"★★★★★".slice(0, Math.round(headerAvg)).padEnd(5, "☆")}
              </span>
              <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
                {t("reviews.countLabel", { count: headerCount })}
              </span>
            </div>
          ) : (
            <div style={{ fontFamily: "var(--font-serif)", fontSize: 15, fontStyle: "italic", color: "var(--ink-3)" }}>
              {t("reviews.zeroState")}
            </div>
          )}
        </div>
      </header>

      {/* Compose / sign-in CTA.
          The compose box is deliberately gated on feedStatus === "ready":
          while the first page is loading we don't yet know whether the
          user has an existing review, so showing the box would flash an
          empty form for users who already reviewed and then snap it
          shut once the feed lands. Worse, they could submit during the
          window and get a 409. */}
      {feedStatus === "ready" && (user ? (
        ownReview ? (
          // If the user already has a row, the compose box is hidden —
          // they can still Edit / Delete via the row's ⋯ menu.
          <div
            style={{
              marginBottom: 24,
              padding: "10px 12px",
              borderRadius: 8,
              background: "var(--ai-bg)",
              border: "1px solid var(--ai-rule)",
              fontSize: 12,
              color: "var(--ink-2)",
            }}
          >
            {t("reviews.alreadyReviewedHint")}
          </div>
        ) : (
          <ComposeBox
            rating={composeRating}
            onRating={setComposeRating}
            body={composeBody}
            onBody={setComposeBody}
            busy={composeBusy}
            err={composeErr}
            byteCount={composeByteCount}
            onSubmit={handleSubmitCompose}
            t={t}
          />
        )
      ) : (
        <SignInCTA onSignIn={() => navigate("/auth")} t={t} />
      ))}

      {/* List */}
      {feedStatus === "loading" ? (
        <div style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>
          {t("common.loading")}
        </div>
      ) : feedStatus === "error" ? (
        <div style={{ fontSize: 13, color: "var(--ink-3)" }}>{t("reviews.feedError")}</div>
      ) : reviews.length === 0 ? (
        // The header zero-state covers the messaging when truly empty.
        // Leave the list area empty — extra copy here would be redundant.
        null
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          {reviews.map((r) => (
            <li key={r.id}>
              {editId === r.id ? (
                <EditRow
                  rating={editRating}
                  onRating={setEditRating}
                  body={editBody}
                  onBody={setEditBody}
                  busy={editBusy}
                  err={editErr}
                  byteCount={editByteCount}
                  onSubmit={handleSubmitEdit}
                  onCancel={handleCancelEdit}
                  t={t}
                />
              ) : (
                <ReviewRow
                  r={r}
                  currentUser={user}
                  menuOpen={openMenuId === r.id}
                  onToggleMenu={() => setOpenMenuId((id) => (id === r.id ? null : r.id))}
                  onEdit={() => handleStartEdit(r)}
                  onDelete={() => handleAskDelete(r.id)}
                  onReport={() => handleAskReport(r.id)}
                  t={t}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Load more */}
      {feedStatus === "ready" && nextCursor && (
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadMoreBusy}
            style={tertiaryBtn(loadMoreBusy)}
          >
            {loadMoreBusy ? t("common.loading") : t("reviews.loadMore")}
          </button>
          {loadMoreErr && (
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--danger, #b14444)" }}>
              {loadMoreErr}
            </div>
          )}
        </div>
      )}

      {/* Delete confirm modal */}
      {deletingId && (
        <Dialog
          title={t("reviews.deleteConfirmTitle")}
          onClose={() => (deleteBusy ? null : setDeletingId(null))}
        >
          <p style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.55, margin: "0 0 16px" }}>
            {t("reviews.deleteConfirmBody")}
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              type="button"
              onClick={() => setDeletingId(null)}
              disabled={deleteBusy}
              style={tertiaryBtn(deleteBusy)}
            >
              {t("reviews.cancel")}
            </button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={deleteBusy}
              style={dangerBtn(deleteBusy)}
            >
              {deleteBusy ? t("reviews.deleting") : t("reviews.deleteConfirm")}
            </button>
          </div>
        </Dialog>
      )}

      {/* Report dialog */}
      {reportingId && (
        <Dialog
          title={t("reviews.reportTitle")}
          onClose={() => (reportBusy ? null : setReportingId(null))}
        >
          <label style={{ display: "block", marginBottom: 12 }}>
            <span style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {t("reviews.reportReasonLabel")}
            </span>
            <select
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value as ReviewReportReason)}
              disabled={reportBusy}
              style={inputStyle()}
            >
              {REVIEW_REPORT_REASONS.map((r) => (
                <option key={r} value={r}>
                  {t(`reviews.reason_${r}`)}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: "block", marginBottom: 16 }}>
            <span style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {t("reviews.reportNoteLabel")}
            </span>
            <textarea
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value)}
              disabled={reportBusy}
              rows={3}
              maxLength={1000}
              placeholder={t("reviews.reportNotePh")}
              style={{ ...inputStyle(), resize: "vertical", minHeight: 60 }}
            />
          </label>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              type="button"
              onClick={() => setReportingId(null)}
              disabled={reportBusy}
              style={tertiaryBtn(reportBusy)}
            >
              {t("reviews.cancel")}
            </button>
            <button
              type="button"
              onClick={handleConfirmReport}
              disabled={reportBusy}
              style={primaryBtn(reportBusy)}
            >
              {reportBusy ? t("reviews.reportSubmitting") : t("reviews.reportSubmit")}
            </button>
          </div>
        </Dialog>
      )}

      {toast && <ToastBubble message={toast} onDismiss={() => setToast(null)} />}
    </section>
  );
}

// ── sub-components ────────────────────────────────────────────────────

function ComposeBox({
  rating,
  onRating,
  body,
  onBody,
  busy,
  err,
  byteCount,
  onSubmit,
  t,
}: {
  rating: number;
  onRating: (n: number) => void;
  body: string;
  onBody: (s: string) => void;
  busy: boolean;
  err: string | null;
  byteCount: number;
  onSubmit: () => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const over = byteCount > REVIEW_BODY_MAX_LEN;
  return (
    <div
      style={{
        marginBottom: 24,
        padding: 16,
        border: "1px solid var(--rule)",
        borderRadius: 10,
        background: "var(--surface)",
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <span style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {t("reviews.composeRating")}
        </span>
        <StarPicker value={rating} onChange={onRating} disabled={busy} t={t} />
      </div>
      <label style={{ display: "block", marginBottom: 12 }}>
        <span style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--ink-2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {t("reviews.composeBody")}
        </span>
        {/* No maxLength here — the cap is measured in UTF-8 bytes (see
            reviewBodyByteLength), and maxLength counts UTF-16 code
            units, which would let a CJK reviewer type past the server
            cap or stop them well before it. The submit-time
            validateReviewInput is the canonical gate. */}
        <textarea
          value={body}
          onChange={(e) => onBody(e.target.value)}
          disabled={busy}
          rows={4}
          placeholder={t("reviews.composeBodyPh")}
          style={{ ...inputStyle(), resize: "vertical", minHeight: 88, fontFamily: "var(--font-serif)" }}
        />
        <div style={{ marginTop: 4, display: "flex", justifyContent: "space-between", fontSize: 11, color: over ? "var(--danger, #b14444)" : "var(--ink-3)" }}>
          <span>{err}</span>
          <span aria-live="polite">{t("reviews.charCount", { count: byteCount, max: REVIEW_BODY_MAX_LEN })}</span>
        </div>
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="button" onClick={onSubmit} disabled={busy} style={primaryBtn(busy)}>
          {busy ? t("reviews.submitting") : t("reviews.submit")}
        </button>
      </div>
    </div>
  );
}

function SignInCTA({ onSignIn, t }: { onSignIn: () => void; t: (k: string) => string }) {
  return (
    <div
      style={{
        marginBottom: 24,
        padding: "14px 16px",
        borderRadius: 10,
        border: "1px solid var(--rule)",
        background: "var(--surface)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <div style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 14, color: "var(--ink-2)" }}>
        {t("reviews.signInCta")}
      </div>
      <button type="button" onClick={onSignIn} style={primaryBtn(false)}>
        {t("reviews.signInBtn")}
      </button>
    </div>
  );
}

function ReviewRow({
  r,
  currentUser,
  menuOpen,
  onToggleMenu,
  onEdit,
  onDelete,
  onReport,
  t,
}: {
  r: Review;
  currentUser: AuthUser | null;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onReport: () => void;
  t: (k: string) => string;
}) {
  const own = !!currentUser && currentUser.id === r.author_user_id;
  // "You" label for own rows surfaces ownership without leaking the
  // raw account id; falls back to the server-hydrated author_name for
  // other people's reviews. Truly empty names degrade to a neutral
  // sentinel so a row never renders with a dangling " · 2 hrs ago".
  const displayName = own
    ? t("reviews.youLabel")
    : r.author_name || t("reviews.anonAuthor");
  return (
    <article
      style={{
        padding: 14,
        border: "1px solid var(--rule)",
        borderRadius: 10,
        background: "var(--surface)",
      }}
    >
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <Avatar name={displayName} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {displayName}
            </div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", display: "flex", alignItems: "center", gap: 6 }}>
              <StarRow value={r.rating} />
              <span aria-hidden>·</span>
              <span>{relativeTime(r.updated_at || r.created_at)}</span>
            </div>
          </div>
        </div>
        <div style={{ position: "relative", flexShrink: 0 }} data-pwreview-menu>
          <button
            type="button"
            aria-label={t("reviews.menuLabel")}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={onToggleMenu}
            style={{
              background: "transparent",
              border: "1px solid var(--rule)",
              borderRadius: 6,
              padding: "3px 8px",
              fontSize: 14,
              color: "var(--ink-3)",
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ⋯
          </button>
          {menuOpen && (
            <div
              role="menu"
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: 4,
                background: "var(--surface)",
                border: "1px solid var(--rule)",
                borderRadius: 8,
                boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                minWidth: 140,
                zIndex: 20,
                padding: 4,
              }}
            >
              {own ? (
                <>
                  <MenuItem onClick={onEdit}>{t("reviews.edit")}</MenuItem>
                  <MenuItem onClick={onDelete} danger>
                    {t("reviews.delete")}
                  </MenuItem>
                </>
              ) : (
                <MenuItem onClick={onReport}>{t("reviews.report")}</MenuItem>
              )}
            </div>
          )}
        </div>
      </header>
      <p
        style={{
          margin: 0,
          fontFamily: "var(--font-serif)",
          fontSize: 14,
          lineHeight: 1.6,
          color: "var(--ink-2)",
          whiteSpace: "pre-wrap",
          // pre-wrap preserves newlines but does not break long
          // unbroken runs — an attacker (or a clueless reviewer)
          // could paste 4000 bytes of an unbroken token and force
          // a horizontal scrollbar across the whole modal. anywhere
          // tells the renderer to break inside the run if it must.
          overflowWrap: "anywhere",
        }}
      >
        {r.body}
      </p>
    </article>
  );
}

function EditRow({
  rating,
  onRating,
  body,
  onBody,
  busy,
  err,
  byteCount,
  onSubmit,
  onCancel,
  t,
}: {
  rating: number;
  onRating: (n: number) => void;
  body: string;
  onBody: (s: string) => void;
  busy: boolean;
  err: string | null;
  byteCount: number;
  onSubmit: () => void;
  onCancel: () => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const over = byteCount > REVIEW_BODY_MAX_LEN;
  return (
    <div
      style={{
        padding: 14,
        border: "1px solid var(--accent)",
        borderRadius: 10,
        background: "var(--surface)",
      }}
    >
      <StarPicker value={rating} onChange={onRating} disabled={busy} t={t} />
      {/* maxLength deliberately absent — see ComposeBox for why. */}
      <textarea
        value={body}
        onChange={(e) => onBody(e.target.value)}
        disabled={busy}
        rows={3}
        style={{ ...inputStyle(), marginTop: 10, resize: "vertical", minHeight: 70, fontFamily: "var(--font-serif)" }}
      />
      <div style={{ marginTop: 4, display: "flex", justifyContent: "space-between", fontSize: 11, color: over ? "var(--danger, #b14444)" : "var(--ink-3)" }}>
        <span>{err}</span>
        <span aria-live="polite">{t("reviews.charCount", { count: byteCount, max: REVIEW_BODY_MAX_LEN })}</span>
      </div>
      <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" onClick={onCancel} disabled={busy} style={tertiaryBtn(busy)}>
          {t("reviews.cancel")}
        </button>
        <button type="button" onClick={onSubmit} disabled={busy} style={primaryBtn(busy)}>
          {busy ? t("reviews.saving") : t("reviews.save")}
        </button>
      </div>
    </div>
  );
}

function MenuItem({ children, onClick, danger }: { children: ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        background: "transparent",
        border: "none",
        padding: "8px 10px",
        fontSize: 12.5,
        color: danger ? "var(--danger, #b14444)" : "var(--ink-2)",
        cursor: "pointer",
        borderRadius: 6,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ai-bg)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {children}
    </button>
  );
}

function StarPicker({
  value,
  onChange,
  disabled,
  t,
}: {
  value: number;
  onChange: (n: number) => void;
  disabled: boolean;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const [hover, setHover] = useState(0);
  const display = hover > 0 ? hover : value;
  return (
    <div role="radiogroup" aria-label={t("reviews.composeRating")} style={{ display: "inline-flex", gap: 4 }}>
      {Array.from({ length: REVIEW_RATING_MAX }, (_, i) => {
        const n = i + 1;
        const filled = display >= n;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={t("reviews.starAria", { n })}
            disabled={disabled}
            onClick={() => onChange(n)}
            onMouseEnter={() => !disabled && setHover(n)}
            onMouseLeave={() => setHover(0)}
            style={{
              background: "transparent",
              border: "none",
              padding: 4,
              cursor: disabled ? "default" : "pointer",
              fontSize: 24,
              lineHeight: 1,
              color: filled ? "var(--accent)" : "var(--rule)",
            }}
          >
            {filled ? "★" : "☆"}
          </button>
        );
      })}
      {value < REVIEW_RATING_MIN && hover === 0 && (
        <span style={{ alignSelf: "center", marginLeft: 6, fontSize: 12, color: "var(--ink-3)" }}>
          {t("reviews.starHint")}
        </span>
      )}
    </div>
  );
}

function StarRow({ value }: { value: number }) {
  // Static stars row used inside an existing review row — no interaction.
  return (
    <span aria-label={`${value} / ${REVIEW_RATING_MAX}`} style={{ color: "var(--accent)", letterSpacing: 1 }}>
      {"★★★★★".slice(0, value).padEnd(REVIEW_RATING_MAX, "☆")}
    </span>
  );
}

// ReviewBadge — compact "★ 4.5 · 12" chip surfaced on project cards
// and modal headers (PROP-76). Hidden when reviewCount === 0 so we
// don't show "0 reviews" noise on the bulk of Explore tiles before
// any user-generated content exists. Title attr gives a screen reader
// / hover the unabbreviated phrasing.
export function ReviewBadge({
  avg,
  count,
  size = "sm",
}: {
  avg: number;
  count: number;
  size?: "sm" | "md";
}) {
  if (count <= 0) return null;
  const fontSize = size === "md" ? 12 : 11;
  const padding = size === "md" ? "3px 8px" : "2px 7px";
  return (
    <span
      title={`${avg.toFixed(1)} / ${REVIEW_RATING_MAX} from ${count} review${count === 1 ? "" : "s"}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding,
        borderRadius: 12,
        background: "var(--accent-soft, var(--ai-bg))",
        color: "var(--accent)",
        fontSize,
        fontFamily: "var(--font-ui)",
        fontWeight: 500,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
        // Keep the chip whole when its flex parent runs out of room
        // (HotRow's 1fr column with a long project name shrinks the
        // meta span; the badge must stay readable instead of being
        // squashed into a narrower-than-content state).
        flexShrink: 0,
      }}
    >
      <span aria-hidden>★</span>
      <span className="tabular-nums">{avg.toFixed(1)}</span>
      <span aria-hidden style={{ opacity: 0.55 }}>·</span>
      <span className="tabular-nums" style={{ opacity: 0.7 }}>{count}</span>
    </span>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join("") || "?";
  return (
    <div
      aria-hidden
      style={{
        width: 32,
        height: 32,
        borderRadius: 32,
        background: "var(--accent-soft, var(--ai-bg))",
        color: "var(--accent)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-serif)",
        fontSize: 12,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

function Dialog({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  // Close on Esc; backdrop click handled by the wrapper click filter.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10001,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          background: "var(--surface)",
          border: "1px solid var(--rule)",
          borderRadius: 12,
          padding: 20,
          fontFamily: "var(--font-ui)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.32)",
        }}
      >
        <h3 style={{ margin: "0 0 14px", fontFamily: "var(--font-serif)", fontSize: 18, color: "var(--ink)" }}>
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}

function ToastBubble({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div
      role="status"
      onClick={onDismiss}
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        background: "var(--ink)",
        color: "var(--surface)",
        padding: "10px 16px",
        borderRadius: 24,
        fontSize: 13,
        fontFamily: "var(--font-ui)",
        zIndex: 10002,
        cursor: "pointer",
        boxShadow: "0 8px 24px rgba(0,0,0,0.24)",
      }}
    >
      {message}
    </div>
  );
}

// ── shared styles ─────────────────────────────────────────────────────

function inputStyle(): CSSProperties {
  return {
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid var(--rule)",
    background: "var(--surface)",
    color: "var(--ink)",
    fontSize: 13,
    fontFamily: "var(--font-ui)",
  };
}

function primaryBtn(busy: boolean): CSSProperties {
  return {
    background: "var(--accent)",
    color: "var(--bubble-me-ink, #fff)",
    border: "none",
    padding: "8px 14px",
    borderRadius: 6,
    fontSize: 12.5,
    fontWeight: 500,
    cursor: busy ? "default" : "pointer",
    opacity: busy ? 0.7 : 1,
  };
}

function tertiaryBtn(busy: boolean): CSSProperties {
  return {
    background: "transparent",
    color: "var(--ink-2)",
    border: "1px solid var(--rule)",
    padding: "7px 12px",
    borderRadius: 6,
    fontSize: 12,
    cursor: busy ? "default" : "pointer",
    opacity: busy ? 0.6 : 1,
  };
}

function dangerBtn(busy: boolean): CSSProperties {
  return {
    background: "var(--danger, #b14444)",
    color: "#fff",
    border: "none",
    padding: "8px 14px",
    borderRadius: 6,
    fontSize: 12.5,
    fontWeight: 500,
    cursor: busy ? "default" : "pointer",
    opacity: busy ? 0.7 : 1,
  };
}
